/**
 * FinaleOverlay — celebration card shown after the slide deck finishes
 * (phase === 'done' on non-DATE templates). Mirrors the visual language
 * of CinematicIntroOverlay but reads as the end-of-show beat:
 *
 *   - Heart-burst rings expand outward
 *   - Confetti rain drifts down from above
 *   - "Your proposal is ready" message
 *   - Two buttons: Restart (re-enters intro) and Close (dismisses modal)
 *
 * The overlay sits at z=24 — above slide content and chrome — and
 * fades in over 600ms.
 */
import { useMemo } from 'react';
import { RotateCcw, X, Heart, Sparkles } from 'lucide-react';
import type { ProposalType } from './runtime/PlayerMachine';
import './finale.css';

interface FinaleOverlayProps {
  proposalType?: ProposalType;
  proposeeName?: string;
  onRestart: () => void;
  onClose: () => void;
}

export function FinaleOverlay({
  proposalType,
  proposeeName,
  onRestart,
  onClose,
}: FinaleOverlayProps) {
  const confetti = useMemo(() => generateConfetti(36), []);
  const message = finalMessage(proposalType);
  return (
    <div
      className="finale-overlay"
      role="dialog"
      aria-label="Proposal complete"
      data-testid="finale-overlay"
    >
      {/* Confetti rain */}
      <div className="finale-confetti" aria-hidden>
        {confetti.map((p) => (
          <span
            key={p.id}
            className="finale-confetti-piece"
            style={{
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              background: p.color,
            }}
          />
        ))}
      </div>

      {/* Heart-burst rings */}
      <div className="finale-heart-burst" aria-hidden>
        <span />
        <span />
        <span />
      </div>

      {/* Center content */}
      <div className="finale-card">
        <div className="finale-heart" aria-hidden>
          <Heart className="h-12 w-12" fill="currentColor" stroke="none" />
        </div>
        <h1 className="finale-title">
          {proposeeName ? `For ${proposeeName},` : 'Your proposal is ready'}
        </h1>
        <p className="finale-message">{message}</p>

        <div className="finale-cta-row">
          <button
            type="button"
            onClick={onRestart}
            className="finale-btn finale-btn-primary"
          >
            <RotateCcw className="h-4 w-4" />
            Replay preview
          </button>
          <button
            type="button"
            onClick={onClose}
            className="finale-btn finale-btn-secondary"
          >
            <X className="h-4 w-4" />
            Close
          </button>
        </div>

        <p className="finale-meta">
          <Sparkles className="h-3 w-3" /> Cinematic preview complete
        </p>
      </div>
    </div>
  );
}

function generateConfetti(count: number): Array<{
  id: number;
  left: number;
  delay: number;
  duration: number;
  color: string;
}> {
  const palette = ['#FF3377', '#FFCC00', '#FF5C94', '#FFD93D', '#64D2FF'];
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: Math.round(Math.random() * 100),
    delay: Math.random() * 1.5,
    duration: 3 + Math.random() * 2,
    color: palette[i % palette.length],
  }));
}

function finalMessage(proposalType?: ProposalType): string {
  switch (proposalType) {
    case 'RELATIONSHIP':
      return 'Your story is ready to share.';
    case 'DATE':
      return 'Your invitation is ready to send.';
    case 'MARRIAGE':
    default:
      return 'Your proposal is ready to share.';
  }
}